import { useState } from 'react';
import FormProvider, { RHFInput } from 'Components/Organisms/HookForm';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { joinProject } from 'Services/user';
import { useTranslation } from 'react-i18next';
import ItemSelector from 'Components/Molecules/ItemSelector/ItemSelector';
import AvatarUser from 'Components/Atoms/AvatarUser/AvatarUser';
import { trackError } from 'Utils/errorMonitoring';
import Button from 'Components/Atoms/Button/Button';

const ChangeWorkspaceJoinProjectForm = ({ onComplete }) => {
    const { t } = useTranslation('common');

    const LoginSchema = Yup.object().shape({
        owner_email: Yup.string().required(t('join-project.email-valid')).email(t('join-project.email-valid')),
    });

    const [hasMultipleProjects, setHasMultipleProjects] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState(null);

    const methods = useForm({
        resolver: yupResolver(LoginSchema),
        defaultValues: {
            owner_email: '',
        },
    });

    const queryClient = useQueryClient();

    const {
        handleSubmit,
        setError,
        getValues,
        formState: { isSubmitting },
    } = methods;

    const findJoinedProject = (predicate) => {
        const allProjects = queryClient.getQueryData(['projects']) ?? [];
        return allProjects.find(predicate) ?? null;
    };

    const onSubmit = async (data) => {
        try {
            await joinProject(data);
            await queryClient.invalidateQueries({ queryKey: ['projects'] });

            const ownerEmail = data.owner_email.toLowerCase();
            const joinedProject = findJoinedProject((project) =>
                project.users?.some((u) => u.role === 'owner' && u.email?.toLowerCase() === ownerEmail),
            );
            onComplete(joinedProject);
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.description === 'multiple_projects') {
                setHasMultipleProjects(true);
                setProjects(error.response.data.info?.items ?? []);
                return;
            }

            if (error.response?.status === 404) {
                setError('owner_email', { type: 'manual', message: t('join-project.email-not-found') });
                return;
            }

            trackError(error);
        }
    };

    const handleSelectProject = async (projectId) => {
        setSelectedProjectId(projectId);

        let joinedProject = null;

        try {
            const values = getValues();
            await joinProject({
                ...values,
                project_id: projectId,
            });
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
            joinedProject = findJoinedProject((project) => project.id === projectId);
        } catch (error) {
            trackError(error);
        } finally {
            setSelectedProjectId(null);
            setProjects([]);
            setHasMultipleProjects(false);

            onComplete(joinedProject);
        }
    };

    if (hasMultipleProjects) {
        return (
            <div className="grid gap-4" style={{ pointerEvents: selectedProjectId ? 'none' : 'all' }}>
                {projects.map((project) => (
                    <ItemSelector
                        customImage={<AvatarUser name={project.name} size={50} />}
                        key={project.id}
                        title={project.name}
                        onClick={() => handleSelectProject(project.id)}
                        isBusy={project.id === selectedProjectId}
                        icon={<div />}
                    />
                ))}
            </div>
        );
    }

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6 mb-6">
                <RHFInput type="email" name="owner_email" label={t('join-project.owner-email')} />
            </div>

            <Button variant="primary" type="submit" className="w-full" isBusy={isSubmitting}>
                {t('join-project.join')}
            </Button>
        </FormProvider>
    );
};

export default ChangeWorkspaceJoinProjectForm;

ChangeWorkspaceJoinProjectForm.propTypes = {
    onComplete: PropTypes.func.isRequired,
};
